import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { useConfig } from '../hooks/useConfig';

// 默认请求配置
const DEFAULT_TIMEOUT = 30000;
const DEFAULT_API_BASE_URL = 'https://holon.aialign.cn';
const DEFAULT_GATEWAY_BASE_URL = 'https://gateway.aialign.cn'; // 'http://10.8.0.125:9000'; // 

// 硬编码业务 token（holon 后端接口使用）
const DEFAULT_TOKEN =
  'hk-34307736966dfdc1a8ff37a3a3d7e95069d500a6fa2dfc56f79789b58f3e26ca';

// localStorage 中 OAuth2 token 的 key（与 @packages/api-client 保持一致）
const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

const getAuthToken = (): string => localStorage.getItem(AUTH_TOKEN_KEY) || '';
const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);

const setAuthToken = (token: string) => localStorage.setItem(AUTH_TOKEN_KEY, token);
const setRefreshToken = (token: string) => localStorage.setItem(REFRESH_TOKEN_KEY, token);

// 解码 JWT payload 中的 exp 字段（Unix 秒时间戳）
const decodeJwtExp = (token: string): number | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
};

// 判断当前 token 是否即将过期（提前 2 小时）
const isTokenExpiringSoon = (thresholdSeconds = 7200): boolean => {
  const token = getAuthToken();
  if (!token) return false;
  const exp = decodeJwtExp(token);
  if (exp === null) return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds >= exp - thresholdSeconds;
};

// 判断是否嵌入模式
const isEmbedMode = (): boolean => sessionStorage.getItem('intellix_embed_mode') === 'true';

// 刷新 token 的状态管理（防并发）
let isRefreshing = false;
let refreshTokenPromise: Promise<void> | null = null;

// 获取运行时网关地址
const getRuntimeGateway = (): string => {
  const configGateway =
    (globalThis as any)._intellix_server_config?.gatewayBaseUrl ||
    (globalThis as any)._template_app_server_config?.gatewayBaseUrl;
  const runtimeGateway =
    (typeof configGateway === 'string' && configGateway.startsWith('http') ? configGateway : null) ||
    import.meta.env.VITE_GATEWAY_BASE_URL ||
    DEFAULT_GATEWAY_BASE_URL;
  return runtimeGateway.endsWith('/') ? runtimeGateway.slice(0, -1) : runtimeGateway;
};

// 调用 OAuth2 refresh_token 端点获取新 token（防并发）
const refreshAccessToken = async (): Promise<void> => {
  if (isRefreshing && refreshTokenPromise) {
    return refreshTokenPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh_token available');
  }

  isRefreshing = true;
  refreshTokenPromise = (async () => {
    try {
      const gateway = getRuntimeGateway();
      const authBaseURL = `${gateway}/aialign-auth-service`;

      const params = new URLSearchParams();
      params.append('grant_type', 'refresh_token');
      params.append('refresh_token', refreshToken);

      const response = await axios.post(
        `${authBaseURL}/oauth2/token`,
        params.toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: 'Basic YWlhbGlnbi13ZWI6YWlhbGlnbi13ZWItc2VjcmV0',
          },
        }
      );

      const data = response.data;
      if (data.access_token) {
        setAuthToken(data.access_token.replace(/^Bearer\s+/i, ''));
      }
      if (data.refresh_token) {
        setRefreshToken(data.refresh_token.replace(/^Bearer\s+/i, ''));
      }
      console.log('[HTTP Client] Token 刷新成功');
    } finally {
      isRefreshing = false;
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

/**
 * 扩展的请求配置，支持 useAuthToken 参数。
 * - useAuthToken: true → 使用登录 token + 切换到 gateway 请求地址 + 主动刷新过期 token
 * - 未设置 / false → 使用硬编码 DEFAULT_TOKEN + 使用 holon 请求地址
 */
export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  /** 是否使用登录 token 替代 DEFAULT_TOKEN，同时切换 baseURL 到 gateway */
  useAuthToken?: boolean;
}

interface ApiErrorPayload {
  message?: string;
  msg?: string;
  error?: string;
}

const normalizeDomain = (domain: string): string => {
  const trimmedDomain = domain.trim();

  return trimmedDomain.endsWith('/')
    ? trimmedDomain.slice(0, -1)
    : trimmedDomain;
};

const getRuntimeApiBaseUrl = (): string => {
  const { serverConfig } = useConfig();
  const configuredBaseUrl =
    (serverConfig as { holonBaseUrl?: string })?.holonBaseUrl ||
    DEFAULT_API_BASE_URL;

  return normalizeDomain(configuredBaseUrl);
};

const getGatewayApiBaseUrl = (): string => {
  const { serverConfig } = useConfig();
  const configuredBaseUrl =
    (serverConfig as { gatewayBaseUrl?: string })?.gatewayBaseUrl ||
    DEFAULT_GATEWAY_BASE_URL;

  return normalizeDomain(configuredBaseUrl);
};

const getBackendErrorMessage = (error: AxiosError<ApiErrorPayload>): string => {
  if (!error.response) {
    return error.message || '网络异常，请稍后重试';
  }

  const { status, data } = error.response;
  const backendMessage =
    (typeof data === 'string' ? data : undefined) ||
    data?.message ||
    data?.msg ||
    data?.error;

  return backendMessage
    ? `请求失败(${status})：${backendMessage}`
    : `请求失败(${status})：${error.message || '未知错误'}`;
};

// 创建 axios 实例
const createHttpClient = (config?: AxiosRequestConfig): AxiosInstance => {
  const instance = axios.create({
    baseURL: getRuntimeApiBaseUrl(),
    timeout: DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
    ...config,
  });

  // 请求拦截器：根据 useAuthToken 参数决定携带哪种 token + 切换请求地址 + 主动刷新过期 token
  instance.interceptors.request.use(
    async (cfg: InternalAxiosRequestConfig) => {
      // 读取自定义配置：useAuthToken 为 true 时使用登录 token + gateway 地址，否则使用 DEFAULT_TOKEN + holon 地址
      const useAuthToken = (cfg as ExtendedAxiosRequestConfig).useAuthToken;
      cfg.baseURL = useAuthToken ? getGatewayApiBaseUrl() : getRuntimeApiBaseUrl();
      console.log('请求拦截器:', useAuthToken, cfg.baseURL);
      
      // useAuthToken 请求：主动检测 token 是否即将过期，若过期则提前刷新
      if (useAuthToken && isTokenExpiringSoon()) {
        try {
          await refreshAccessToken();
        } catch {
          // 刷新失败，触发未授权事件
          if (isEmbedMode()) {
            window.dispatchEvent(new CustomEvent('auth:embed-unauthorized'));
          } else {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          return Promise.reject(new Error('Token refresh failed'));
        }
      }

      const token = useAuthToken ? getAuthToken() : DEFAULT_TOKEN;

      if (token) {
        cfg.headers.set('Authorization', `Bearer ${token}`);
      }

      // 清除自定义参数，避免发送到服务端
      delete (cfg as ExtendedAxiosRequestConfig).useAuthToken;

      return cfg;
    },
    (error: unknown) => Promise.reject(error)
  );

  // 响应拦截器：统一处理错误 + 401 认证失效
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.status < 200 || response.status >= 300) {
        return Promise.reject(
          new Error(`请求失败(${response.status})：HTTP 状态码异常`)
        );
      }

      return response;
    },
    (error: AxiosError<ApiErrorPayload>) => {
      // 401 认证失效 → 触发全局事件，由 ProtectedRoute 处理跳转
      if (error.response?.status === 401) {
        if (isEmbedMode()) {
          window.dispatchEvent(new CustomEvent('auth:embed-unauthorized'));
        } else {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
      }

      return Promise.reject(new Error(getBackendErrorMessage(error)));
    }
  );

  return instance;
};

// 导出默认 http 客户端实例
export const http = createHttpClient();

export { DEFAULT_TOKEN, getAuthToken };

export default createHttpClient;
